import { useState, useEffect, useRef } from 'react';
import { Send, Edit2, Trash2, X, Check, MessageSquare } from '../../icons/wp-icons';
import toast from 'react-hot-toast';
import apiClient from '../../api/apiClient';

export default function ProjektNotizen({ projektId }) {
  const [notizen, setNotizen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNotiz, setNewNotiz] = useState('');
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    loadNotizen();
  }, [projektId]);

  const loadNotizen = async () => {
    try {
      const response = await apiClient.get(`/projekte/${projektId}/notizen`);
      setNotizen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Notizen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newNotiz.trim() || sending) return;

    setSending(true);
    try {
      const response = await apiClient.post(`/projekte/${projektId}/notizen`, {
        text: newNotiz.trim()
      });
      setNotizen([...notizen, response.data]);
      setNewNotiz('');
      inputRef.current?.focus({ preventScroll: true });
    } catch (error) {
      toast.error('Fehler beim Senden der Notiz');
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (notiz) => {
    setEditingId(notiz.id);
    setEditText(notiz.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveEdit = async (notizId) => {
    if (!editText.trim()) return;

    try {
      const response = await apiClient.put(`/projekte/${projektId}/notizen/${notizId}`, {
        text: editText.trim()
      });
      setNotizen(notizen.map(n => n.id === notizId ? response.data : n));
      setEditingId(null);
      setEditText('');
      toast.success('Notiz aktualisiert');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (notizId) => {
    if (!confirm('Notiz wirklich loeschen?')) return;

    try {
      await apiClient.delete(`/projekte/${projektId}/notizen/${notizId}`);
      setNotizen(notizen.filter(n => n.id !== notizId));
      toast.success('Notiz geloescht');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();

    const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    if (isToday) {
      return `Heute, ${time}`;
    } else if (isYesterday) {
      return `Gestern, ${time}`;
    } else {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Gruppiere Nachrichten nach Autor fuer zusammenhaengende Anzeige
  const groupedNotizen = notizen.reduce((acc, notiz, index) => {
    const prev = notizen[index - 1];
    const showAuthor = !prev || prev.benutzerId !== notiz.benutzerId ||
      (new Date(notiz.erstelltAm) - new Date(prev.erstelltAm)) > 300000; // 5 min

    acc.push({ ...notiz, showAuthor });
    return acc;
  }, []);

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden flex flex-col" style={{ maxHeight: '500px' }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-text">Projekt-Notizen</h3>
        <span className="text-sm text-text-muted">({notizen.length})</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
        {notizen.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-text-muted py-8">
            <MessageSquare className="w-12 h-12 mb-3 opacity-30" />
            <p>Noch keine Notizen</p>
            <p className="text-sm">Schreiben Sie die erste Notiz zu diesem Projekt</p>
          </div>
        ) : (
          groupedNotizen.map((notiz) => (
            <div key={notiz.id} className={`flex gap-3 ${notiz.showAuthor ? 'mt-4' : 'mt-1'}`}>
              {/* Avatar */}
              <div className="flex-shrink-0">
                {notiz.showAuthor ? (
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                    {getInitials(notiz.benutzer?.name)}
                  </div>
                ) : (
                  <div className="w-8" /> // Placeholder fuer Ausrichtung
                )}
              </div>

              {/* Message Content */}
              <div className="flex-1 min-w-0">
                {notiz.showAuthor && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-text text-sm">{notiz.benutzer?.name || 'Unbekannt'}</span>
                    <span className="text-xs text-text-muted">{formatTime(notiz.erstelltAm)}</span>
                  </div>
                )}

                {editingId === notiz.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text text-sm resize-none focus:ring-2 focus:ring-primary focus:border-primary"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(notiz.id)}
                        className="px-3 py-1 text-xs bg-primary text-white rounded-button hover:bg-primary-dark transition flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" />
                        Speichern
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 text-xs border border-border rounded-button hover:bg-surface-elevated transition flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="group relative">
                    <div className="bg-surface-elevated rounded-lg px-3 py-2 inline-block max-w-full">
                      <p className="text-text text-sm whitespace-pre-wrap break-words">{notiz.text}</p>
                      {notiz.aktualisiertAm !== notiz.erstelltAm && (
                        <p className="text-xs text-text-muted mt-1 italic">bearbeitet</p>
                      )}
                    </div>

                    {/* Edit/Delete Buttons */}
                    <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition flex gap-1">
                      <button
                        onClick={() => handleStartEdit(notiz)}
                        className="p-1 text-text-muted hover:text-primary transition bg-surface rounded"
                        title="Bearbeiten"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(notiz.id)}
                        className="p-1 text-text-muted hover:text-red-500 transition bg-surface rounded"
                        title="Loeschen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-border bg-surface-elevated">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newNotiz}
            onChange={(e) => setNewNotiz(e.target.value)}
            placeholder="Notiz schreiben..."
            className="flex-1 px-3 py-2 border border-border rounded-button bg-surface text-text text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newNotiz.trim() || sending}
            className="px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
